import React, {useState, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {regular, solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import useAuth from "../../../../hooks/useAuth";
import { useEffectOnce } from "../../../../hooks/useEffectOnce";
import { useNavigate, useLocation } from "react-router-dom";
import {toast} from "sonner";
import {Dialog, DialogHeader, DialogContent, DialogTitle, DialogFooter} from "../../../../components/ui/dialog";
import {Label} from "../../../../components/ui/label";
import {Input} from "../../../../components/ui/input";
import {Textarea} from "../../../../components/ui/textarea";
import {Button} from "../../../../components/ui/button";
import {config} from "../../../../Environment";
import { decimalAmount, currencyAmount } from "../../../../lib/currency";
import {ChevronDown, ChevronUp, Sparkles, ShoppingCart, Zap, Wand2, CheckCircle2, ExternalLink, CreditCard} from "lucide-react";
import { GeneratingLoader } from "../../../ui/generating-loader";
import ActivitySlider from "../../../activityComponents/ActivitySlider";
import { PaymentIframe } from "../../../payment/PaymentIframe";
import { useCompanion } from "../../../../context/CompanionProvider";
import { getCompanionMessageConfig } from "../../../../lib/companionGuide";
import type { CompanionMessage } from "../../../../lib/companionGuide";
import CompanionBanner from "../../../companion/CompanionBanner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuItem,
  DropdownMenuContent,
} from "../../../ui/dropdown-menu";

type WizardPrefillData = {
    fromState?: string;
    toState?: string;
    title?: string;
    intro?: string;
    purpose?: string;
    source?: string;
};

type AiAuthoringWizardProps = {
    open: boolean;
    onClose: () => void;
    isMobile: boolean;
    refreshActivities?: () => void;
    runPrescriptionById?: (id: any) => void;
    prefillData?: WizardPrefillData | null;
    autoOpen?: boolean;
};

const AiAuthoringWizardDialog: React.FC<AiAuthoringWizardProps> = ({
                                                                       open,
                                                                       onClose,
                                                                       isMobile,
                                                                       refreshActivities,
                                                                       runPrescriptionById,
                                                                       prefillData,
                                                                       autoOpen
                                                                   }) => {
    const { auth } = useAuth();
    const {t} = useTranslation();
    const retryFetch = useRetryFetch();
  const navigate = useNavigate();
    const { userStage, dismissMessage } = useCompanion();
    const [wizardBannerDismissed, setWizardBannerDismissed] = useState(() => {
        try {
            const stored = localStorage.getItem("companion_dismissed");
            const dismissed: string[] = stored ? JSON.parse(stored) : [];
            return dismissed.includes("ai_wizard_intro");
        } catch { return false; }
    });

    // Companion banner for the wizard
    const wizardCompanionMessage = useMemo(() => {
        if (wizardBannerDismissed) return null;
        const cfg = getCompanionMessageConfig("ai_wizard", userStage);
        if (!cfg) return null;
        return {
            ...cfg,
            title: cfg.titleKey ? t(cfg.titleKey) : "",
            message: t(cfg.messageKey),
            action: cfg.action
                ? { ...cfg.action, label: t(cfg.action.labelKey), route: cfg.action.route }
                : undefined,
        } as CompanionMessage;
    }, [userStage, t, wizardBannerDismissed]);

    const handleWizardBannerDismiss = (id: string) => {
        dismissMessage(id);
        setWizardBannerDismissed(true);
    };

    // Form state
    const [activityPurpose, setActivityPurpose] = useState("");

    // Wizard state
    const [currentStep, setCurrentStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [isUserProfileLoading, setIsUserProfileLoading] = useState(false);
    const [userProfile, setUserProfile] = useState<any>(null);
    const [aiSuggestion, setAiSuggestion] = useState<any>(null);
    const [createdActivity, setCreatedActivity] = useState<any>(null);
    const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
    const [activityStepsLimit, setActivityStepsLimit] = useState<any>(null);
    const [settings, setSettings] = useState<any>({});
    const [detectedLanguage, setDetectedLanguage] = useState<string>('en');
    const [credits, setCredits] = useState<number>(0);
    const [isWizardEnabled, setIsWizardEnabled] = useState<boolean>(false);
    const [products, setProducts] = useState<any>(null);

    // Payment state
    const [showPaymentIframe, setShowPaymentIframe] = useState(false);
    const [paymentUrl, setPaymentUrl] = useState("");

    useEffectOnce(() => {
        fetchUserProfile();
    }, []);

    // Handle prefill data from marketing site
    useEffectOnce(() => {
        if (prefillData) {
            // Build the purpose from fromState/toState if available
            if (prefillData.fromState && prefillData.toState) {
                const purpose = prefillData.purpose ||
                    `I want to transform from "${prefillData.fromState}" to "${prefillData.toState}"`;
                setActivityPurpose(purpose);
            }

            // If we have a pre-generated suggestion (title + intro), use it
            if (prefillData.title && prefillData.intro) {
                setAiSuggestion({
                    title: prefillData.title,
                    intro: prefillData.intro
                });
                // Skip to step 2 since we already have the suggestion
                setCurrentStep(2);
            }

            // Track analytics
            if (prefillData.source === 'marketing_site') {
                console.log('[Wizard] Prefilled from marketing site:', prefillData);
                // You can add analytics tracking here
                // trackEvent('wizard_prefilled_from_marketing', { ...prefillData });
            }

            // NOTE: We DON'T clear sessionStorage here because:
            // 1. User might need to sign up first (data must survive auth flow)
            // 2. User might close wizard without completing (they can resume later)
            // We clear it only when the activity is successfully created
        }
    }, [prefillData]);

    // Simple language detection based on character sets
    const detectLanguage = (text: string): string => {
        // Hebrew detection
        const hebrewRegex = /[\u0590-\u05FF]/;
        if (hebrewRegex.test(text)) {
            return 'he';
        }

        // German detection (common umlauts and special characters)
        const germanRegex = /[äöüßÄÖÜ]/;
        if (germanRegex.test(text)) {
            return 'de';
        }

        // Default to English
        return 'en';
    };

    const handleNext = async () => {
        if (!activityPurpose.trim()) {
            toast.error(t("activity_purpose_required_for_save"), {
                className: "!bg-red-500 !text-white",
            });
            return;
        }

        // Detect language from input
        const detected = detectLanguage(activityPurpose);
        setDetectedLanguage(detected);

        setIsLoading(true);
        try {
            // Call your AI suggestion API with language setting
            const response = await retryFetch(
                `${config.API_BASE}/api/ai/generate-suggestion`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                    body: JSON.stringify({
                        purpose: activityPurpose,
                        settings: {
                            ...settings,
                            language: detected,
                        },
                    }),
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || "Failed to generate suggestion");
            }

            // Store the AI suggestion
            setAiSuggestion(data.conceptSuggestion);

            // Move to next step
            setCurrentStep(2);

        } catch (error) {
            console.error("Error generating suggestion:", error);
            toast.error(t("error_generating_suggestion", {error: error}));
        } finally {
            setIsLoading(false);
        }
    };

    async function fetchUserProfile() {
        try {
            setIsUserProfileLoading(true);
            const response = await retryFetch(`${config.API_BASE}/api/user/profile`);
            if (!response.ok) {
                throw new Error(`Response status ${response.status} ${response.statusText}`);
            }
            const responseData = await response.json();
            if (responseData.type === 'item') {
                setUserProfile(responseData.item);
                const available = responseData.item.availableActivityGeneratorCredits ?? 0;
                setCredits(available);
                if (available > 0) {
                    setIsWizardEnabled(true);
                } else {
                    setIsWizardEnabled(false);
                    await getProducts();
                }
            } else {
                setUserProfile(null);
                throw new Error(`Unsupported response format: ${JSON.stringify(responseData)}`);
            }
        } catch (err: any) {
            console.log(`[AI Authoring Wizard] fetchUserProfile failed: ${err.message}`);
            toast("Failed to load user profile");
        } finally {
            setIsUserProfileLoading(false);
        }
    }

    async function getProducts() {
        const response = await retryFetch(
        `${config.API_BASE}/api/product/searchActive?type=activity_generator_credit`
        );
        const responseMessage = await response.json();
        if (Array.isArray(responseMessage.list)) {
            setProducts(responseMessage.list);
        }
    }

    async function buyCredits(idx: number) {
        if (!Array.isArray(products)) {
            toast("Loading...");
            return;
        }
        if (idx >= products.length) {
            toast("Error");
            return;
        }
        const productId = products[idx]._id.toString();

        // Start payment process
        try {
            const response = await retryFetch(`${config.API_BASE}/api/payment/start`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({
                    productId,
                    useIframe: true,
                }),
            });

            if (response.ok) {
                const result = await response.json();
                if (result.type === 'redirect') {
                    console.log(`Opening payment in iframe: ${result.redirect}`);
                    setPaymentUrl(result.redirect);
                    setShowPaymentIframe(true);
                }
            } else {
                toast.error(t("payment_initialization_failed") || "Failed to initialize payment");
            }
        } catch (error) {
            console.error("Error starting payment:", error);
            toast.error(t("payment_initialization_failed") || "Failed to initialize payment");
        }
    }

    function handlePaymentSuccess() {
        // Payment successful - refresh user profile to get updated credits
        setShowPaymentIframe(false);
        toast.success(t("payment_successful") || "Payment successful!");

        // Refresh user profile to get updated credits
        fetchUserProfile().then(() => {
            // After credits are refreshed, enable the wizard
            toast.success(t("credits_updated") || "Credits updated! You can now create your activity.");
        });
    }

    function handlePaymentError(error: string) {
        setShowPaymentIframe(false);
        toast.error(error || t("payment_failed") || "Payment failed");
    }

    function handlePaymentClose() {
        setShowPaymentIframe(false);
    }


    // Handle creating the activity with AI suggestion
    const handleCreateActivity = async () => {
        setIsLoading(true);
        try {
            const response = await retryFetch(
                `${config.API_BASE}/api/ai/create-from-suggestion`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                    body: JSON.stringify({
                        purpose: activityPurpose,
                        suggestion: aiSuggestion,
                        stepsLimit: activityStepsLimit,
                        settings: {
                            ...settings,
                            language: detectedLanguage,
                        }
                    }),
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || "Failed to create activity");
            }

            setCreatedActivity(data?.savedActivity);

            toast.success(t("activity_created_successfully", {name: data?.savedActivity?.name}));

            // Clear wizard data from sessionStorage so it doesn't auto-open again
            sessionStorage.removeItem('clary_wizard_prefill');
            sessionStorage.removeItem('clary_wizard_auto_open');

            // Refresh the activities list if a refresh function was provided
            if (refreshActivities) {
                refreshActivities();
            }

            setCurrentStep(3);

            // Close the wizard
            // onClose();

        } catch (error) {
            console.error("Error creating activity:", error);
            toast.error(t("error_creating_activity") || "Error creating activity");
        } finally {
            setIsLoading(false);
        }
    };

    const resetWizard = () => {
        setCurrentStep(1);
        setAiSuggestion(null);
    };

    const renderStepIndicator = () => {
        return (
            <div className="flex justify-center items-center">
                {[1, 2, 3].map((step) => (
                    <React.Fragment key={step}>
                        <div
                            className={`${currentStep >= step ? 'bg-primary' : 'bg-gray-300'} w-4 h-4 rounded-full flex items-center justify-center`}>
                            {(currentStep > step || currentStep === 3) && (
                                <FontAwesomeIcon
                                    icon={solid("check")}
                                    className="text-white text-[9px]"
                                />
                            )}
                        </div>
                        {step < 3 && (
                            <div className={`w-8 h-1 ${currentStep >= step + 1 ? 'bg-primary' : 'bg-gray-300'}`}></div>
                        )}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    const renderStepHeader = () => {
        switch (currentStep) {
            case 1: {
                return (
                    <div className="space-y-2 sm:space-y-3">
                        <DialogTitle className="flex items-center gap-2 text-lg sm:text-2xl">
                            <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-primary/20 to-primary/30">
                                <Wand2 className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                            </div>
                            {t("create_with_ai")}
                        </DialogTitle>
                        {prefillData && (
                            <div className="flex items-start gap-2 px-3 py-2 bg-primary/5 border border-primary/20 rounded-lg">
                                <Sparkles className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                                <span className="text-xs sm:text-sm text-primary font-medium">
                                    We've started your workflow based on your preview!
                                </span>
                            </div>
                        )}
                        <div className="flex items-center gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-primary/5 border border-primary/20 rounded-lg">
                            <FontAwesomeIcon icon={solid("flask")} className="text-primary text-xs sm:text-sm"/>
                            <span className="text-xs text-primary font-medium">
                                {t("experimental_feature")}
                            </span>
                        </div>
                        <div className="flex items-center gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-primary/5 border border-primary/20 rounded-lg">
                            <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary"/>
                            <span className="text-xs sm:text-sm text-gray-700">
                                <span className="font-semibold text-primary">{credits}</span> {t("credits_available")}
                            </span>
                        </div>
                    </div>
                )
            }
            case 2:
                return (
                    <div className="space-y-2 sm:space-y-3">
                        <DialogTitle className="flex items-center gap-2 text-lg sm:text-2xl">
                            <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-primary"/>
                            {t("ai_suggestion") || "AI Suggestion"}
                        </DialogTitle>
                        <Input
                            value={aiSuggestion?.title}
                            onChange={(e) => setAiSuggestion({...aiSuggestion, title: e.target.value})}
                            className="text-sm sm:text-base font-medium"
                        />
                    </div>
                );
            case 3:
                return null;
        }
    }

    function renderIsUserProfileLoading() {
        if (isUserProfileLoading) {
            return (
                <div>
                <FontAwesomeIcon icon={solid("spinner")} className="mr-2 animate-spin"/>
                <span>{t("loading")}</span>
                </div>
            );
        } else {
            return (<></>);
        }
    }

    function renderProducts() {
        if (!Array.isArray(products)) {
            return (
                <div className="flex items-center justify-center py-8">
                    <FontAwesomeIcon icon={solid("spinner")} className="mr-2 animate-spin text-primary"/>
                    <span className="text-sm text-gray-600">{t("loading")}</span>
                </div>
            );
        }
        if (products.length === 0) {
            return (
                <div className="text-center py-8 text-gray-500">
                    <p>{t("no_products_available")}</p>
                </div>
            );
        }

        return (
            <div className="grid gap-2 sm:gap-3">
                {products.map((product: any, i: number) => (
                    <div
                        key={product._id}
                        className="relative group bg-gradient-to-br from-primary/5 to-primary/10 hover:from-primary/10 hover:to-primary/20 border-2 border-primary/20 hover:border-primary/40 rounded-xl p-3 sm:p-5 transition-all duration-300 cursor-pointer hover:shadow-lg hover:scale-[1.02]"
                        onClick={() => buyCredits(i)}
                    >
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                            <div className="flex items-center gap-3 sm:gap-4">
                                <div className="flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
                                    <Zap className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-1.5 sm:gap-2">
                                        <span className="text-xl sm:text-2xl font-bold text-gray-900">
                                            {product.data.activity_generator_credit.amount}
                                        </span>
                                        <span className="text-xs sm:text-sm text-gray-600">
                                            {t("credits")}
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        {t("ai_activity_generations")}
                                    </p>
                                </div>
                            </div>
                            <div className="flex flex-row sm:flex-col items-center sm:items-end gap-2 w-full sm:w-auto">
                                <span className="text-xl sm:text-2xl font-bold text-primary flex-1 sm:flex-none">
                                    {currencyAmount(product.price.amount, product.price.currency)}
                                </span>
                                <Button
                                    size="sm"
                                    className="gap-1.5 w-full sm:w-auto"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        buyCredits(i);
                                    }}
                                >
                                    <CreditCard className="h-4 w-4" />
                                    {t("buy")}
                                </Button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    function renderPurchaseCredits() {
        if (isWizardEnabled) {
            return (<></>);
        }
        return (
            <DialogContent className="max-w-[95vw] sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    {renderIsUserProfileLoading()}
                    <DialogTitle className="flex items-center gap-2 text-lg sm:text-2xl">
                        <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-primary/20 to-primary/30">
                            <Wand2 className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                        </div>
                        {t("ai_assistance")}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 sm:space-y-4">
                    {/* Credit Status */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4">
                        <div className="flex items-center gap-2 sm:gap-3">
                            <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white border-2 border-primary/20">
                                <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                            </div>
                            <div>
                                <p className="text-xs sm:text-sm font-medium text-gray-900">
                                    {t("current_balance")}
                                </p>
                                <p className="text-xl sm:text-2xl font-bold text-primary">
                                    {credits} {t("credits")}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Info Message */}
                    <div className="text-center py-1 sm:py-2">
                        <p className="text-sm sm:text-base font-medium text-gray-900 mb-1">
                            {t("buy_credits_to_create")}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-500">
                            {t("each_credit_one_activity")}
                        </p>
                    </div>

                    {/* Products */}
                    {renderProducts()}
                </div>
                <DialogFooter className="sm:justify-center">
                </DialogFooter>
            </DialogContent>
        );
    }

    function renderAuthoringWizard() {
        if (!isWizardEnabled) {
            return (<></>);
        }
        return (
            <DialogContent className="max-w-[95vw] sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    {renderStepHeader()}
                </DialogHeader>
                {currentStep === 1 && isLoading && (
                    <div className="py-8">
                        <GeneratingLoader context="prompt" />
                    </div>
                )}
                {currentStep === 1 && !isLoading && (
                    <div className="space-y-4 sm:space-y-5">
                        {wizardCompanionMessage && (
                            <CompanionBanner
                                message={wizardCompanionMessage}
                                onDismiss={handleWizardBannerDismiss}
                                compact
                            />
                        )}
                        <div className="space-y-2 sm:space-y-3">
                            <Label htmlFor="activityPurpose" className="text-sm sm:text-base font-medium">
                                {t("what_activity_create")}
                            </Label>
                            <Textarea
                                id="activityPurpose"
                                value={activityPurpose}
                                onChange={(e) => setActivityPurpose(e.target.value)}
                                rows={isMobile ? 5 : 4}
                                className="text-sm sm:text-base resize-none"
                                placeholder={t("describe_activity_purpose_placeholder")}
                            />
                            <p className="text-xs text-gray-500">
                                {t("ai_will_generate_suggestion")}
                            </p>
                        </div>
                        <Button
                            onClick={handleNext}
                            disabled={isLoading || !activityPurpose.trim()}
                            type="button"
                            size={isMobile ? "default" : "lg"}
                            className="w-full gap-2"
                        >
                            {isLoading ? (
                                <>
                                    <FontAwesomeIcon icon={solid("spinner")} className="animate-spin"/>
                                    <span className="text-sm sm:text-base">{t("generating")}</span>
                                </>
                            ) : (
                                <>
                                    <Wand2 className="w-4 h-4 sm:w-5 sm:h-5"/>
                                    <span className="text-sm sm:text-base">{t("generate_suggestion")}</span>
                                </>
                            )}
                        </Button>
                    </div>
                )}
                {currentStep === 2 && isLoading && (
                    <div className="py-8">
                        <GeneratingLoader context="activity" />
                    </div>
                )}
                {currentStep === 2 && !isLoading && (
                    <div className="space-y-4 sm:space-y-5">
                        <div className="space-y-2 sm:space-y-3">
                            <Label className="text-xs sm:text-sm font-medium text-gray-700">
                                {t("review_and_refine")}
                            </Label>
                            <Textarea
                                id="activityIntro"
                                value={aiSuggestion?.intro}
                                onChange={(e) => setAiSuggestion({...aiSuggestion, intro: e.target.value})}
                                rows={isMobile ? 8 : 6}
                                className="text-sm sm:text-base"
                            />
                        </div>
                        <div className="border-t border-gray-200 pt-2 sm:pt-3">
                           <Button
                               variant="ghost"
                               onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                               type="button"
                               className="w-full justify-between hover:bg-gray-50 text-xs sm:text-sm"
                           >
                               <span className="flex items-center gap-1.5 sm:gap-2">
                                   <FontAwesomeIcon icon={solid("sliders")} className="w-3.5 h-3.5 sm:w-4 sm:h-4"/>
                                   {t("advanced_settings") || "Advanced Settings"}
                               </span>
                               {showAdvancedSettings ?
                                   <ChevronUp className="h-3.5 w-3.5 sm:h-4 sm:w-4 transition-transform duration-200" /> :
                                   <ChevronDown className="h-3.5 w-3.5 sm:h-4 sm:w-4 transition-transform duration-200" />
                               }
                           </Button>
                           {showAdvancedSettings && (
                                   <div className="space-y-3 sm:space-y-4 mt-3 sm:mt-4 p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200">
                                       <div className="space-y-2">
                                           <Label htmlFor="language" className="text-sm font-medium">
                                               {t('quick_activity_language') || 'Language'}
                                           </Label>
                                           <div className="flex items-center gap-2">
                                               <select
                                                   id="language"
                                                   className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm"
                                                   value={detectedLanguage}
                                                   onChange={(e) => setDetectedLanguage(e.target.value)}
                                               >
                                                   <option value="en">English (en)</option>
                                                   <option value="he">עברית (he)</option>
                                                   <option value="de">Deutsch (de)</option>
                                               </select>
                                               <span className="text-xs text-gray-500 whitespace-nowrap">
                                                   {t('auto_detected') || 'Auto-detected'}
                                               </span>
                                           </div>
                                       </div>

                                       <div className="space-y-2">
                                           <Label htmlFor="tone" className="text-sm font-medium">
                                               {t('quick_activity_settings_tone')}
                                           </Label>
                                           <Input
                                              type="text" id="tone" value={settings['tone']}
                                              placeholder="formal | reflective | coaching | playful"
                                              onChange={(event)=> settings['tone'] = event.target.value}
                                           />
                                       </div>

                                       <div className="space-y-2">
                                           <Label htmlFor="style" className="text-sm font-medium">
                                               {t('quick_activity_settings_style')}
                                           </Label>
                                           <Input type="text" id="style" value={settings['style']}
                                              placeholder="simple | complex | practical"
                                              onChange={(event)=> settings['style'] = event.target.value}
                                           />
                                       </div>

                                       <div className="space-y-2">
                                           <Label htmlFor="focus" className="text-sm font-medium">
                                               {t('quick_activity_settings_focus')}
                                           </Label>
                                           <Input type="text" id="focus" value={settings['focus']}
                                              placeholder="self-awareness | action-planning | problem-solving"
                                              onChange={(event)=> settings['focus'] = event.target.value}
                                           />
                                       </div>

                                       <div className="space-y-2">
                                           <Label htmlFor="additional" className="text-sm font-medium">
                                               {t('quick_activity_settings_additional_info')}
                                           </Label>
                                           <Textarea id="additional" value={settings['additional_info']}
                                              onChange={(event)=> settings['additional_info'] = event.target.value}
                                              rows={2}
                                           />
                                       </div>

                                       <div className="space-y-2">
                                           <Label className="text-xs sm:text-sm font-medium">
                                               {t("steps_limit_for_activity")}
                                           </Label>
                                           <ActivitySlider
                                               className="mt-2"
                                               itemkey="stepsLimit"
                                               content={{
                                                   width: isMobile ? "100%" : "350px",
                                                   min: 2,
                                                   max: 10,
                                                   step: 1,
                                               }}
                                               defaultValue={3}
                                               onChange={(key: any, value: any) => setActivityStepsLimit(value)}
                                           />
                                       </div>
                                   </div>
                           )}
                       </div>

                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                            <Button
                                variant="outline"
                                onClick={resetWizard}
                                type="button"
                                className="flex-1 text-sm sm:text-base"
                                size={isMobile ? "default" : "lg"}
                            >
                                {t("back") || "Back"}
                            </Button>
                            <Button
                                onClick={handleCreateActivity}
                                disabled={isLoading}
                                type="button"
                                className="flex-1 sm:flex-[2] gap-2"
                                size={isMobile ? "default" : "lg"}
                            >
                                {isLoading ? (
                                    <>
                                        <FontAwesomeIcon icon={solid("spinner")} className="animate-spin"/>
                                        <span className="text-sm sm:text-base">{t("creating")}</span>
                                    </>
                                ) : (
                                    <>
                                        <Wand2 className="w-4 h-4 sm:w-5 sm:h-5"/>
                                        <span className="text-sm sm:text-base">{t("create_activity")}</span>
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                )}
                {currentStep === 3 && (
                    <div className="space-y-4 sm:space-y-6">
                        {/* Success Animation */}
                        <div className="flex flex-col justify-center items-center gap-3 sm:gap-4 text-center py-4 sm:py-6">
                            <div className="relative">
                                <div className="absolute inset-0 bg-green-400/20 rounded-full blur-2xl animate-pulse"></div>
                                <div className="relative flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 shadow-lg">
                                    <CheckCircle2 className="w-12 h-12 sm:w-14 sm:h-14 text-white" strokeWidth={2.5}/>
                                </div>
                            </div>

                            <div className="space-y-1 sm:space-y-2">
                                <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                                    {t("success_activity_created")}
                                </h3>
                                <p className="text-base sm:text-lg text-gray-700 font-medium px-2">
                                    {createdActivity?.name}
                                </p>
                                <p className="text-xs sm:text-sm text-gray-500">
                                    {t("activity_ready_to_use")}
                                </p>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-col w-full gap-2 sm:gap-3 mt-2 sm:mt-4">
                                <Button
                                    size={isMobile ? "default" : "lg"}
                                    className="w-full gap-2"
                                    onClick={() => {
                                        // Clear wizard data before starting activity
                                        sessionStorage.removeItem('clary_wizard_prefill');
                                        sessionStorage.removeItem('clary_wizard_auto_open');

                                        if (runPrescriptionById && createdActivity._id) {
                                            runPrescriptionById(createdActivity._id);
                                        }
                                    }}
                                >
                                    <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
                                    <span className="text-sm sm:text-base">{t("start_activity_now")}</span>
                                </Button>
                                <Button
                                    size={isMobile ? "default" : "lg"}
                                    variant="outline"
                                    className="w-full text-sm sm:text-base"
                                    onClick={() => {
                                        // Clear wizard data
                                        sessionStorage.removeItem('clary_wizard_prefill');
                                        sessionStorage.removeItem('clary_wizard_auto_open');
                                        onClose();
                                    }}
                                >
                                    {t("back_to_my_activities")}
                                </Button>
                            </div>
                        </div>

                        {/* Authoring License Info */}
                        <div className="bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-xl p-3 sm:p-5 space-y-2 sm:space-y-3">
                            <div className="flex items-start gap-2 sm:gap-3">
                                <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary/10 flex-shrink-0">
                                    <FontAwesomeIcon icon={solid("pen-fancy")} className="text-primary w-4 h-4 sm:w-5 sm:h-5"/>
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-1">
                                        {t("authoring_license_title")}
                                    </h4>
                                    <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                                        {t("authoring_license_description")}
                                    </p>
                                </div>
                            </div>
                            <Button
                                variant="link"
                                className="gap-1.5 sm:gap-2 p-0 h-auto text-primary text-xs sm:text-sm"
                                onClick={() => {
                                    window.open('/marketplace?tab=platform', '_blank');
                                }}
                            >
                                {t("view_marketplace_subscriptions")}
                                <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </Button>
                        </div>
                    </div>
                )}                
                <DialogFooter className="sm:justify-center">
                    {renderStepIndicator()}
                </DialogFooter>
            </DialogContent>
        );
    }

    return (
        <>
            <Dialog open={open} onOpenChange={(isOpen) => {
                if (!isOpen) {
                    // Clear wizard data when closing
                    sessionStorage.removeItem('clary_wizard_prefill');
                    sessionStorage.removeItem('clary_wizard_auto_open');
                    onClose();
                }
            }}>
                {renderPurchaseCredits()}
                {renderAuthoringWizard()}
            </Dialog>

            <PaymentIframe
                open={showPaymentIframe}
                paymentUrl={paymentUrl}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
                onClose={handlePaymentClose}
            />
        </>
    );
};
export default AiAuthoringWizardDialog;